import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import type {} from '@mui/material/themeCssVarsAugmentation';
import { mixins } from '../mixins/mixins';
import { muiAccordionDetailsOverride } from '../mui-overrides/mui-accordion-details.override';
import { muiAccordionSummaryOverride } from '../mui-overrides/mui-accordion-summary.override';
import { muiAccordionOverride } from '../mui-overrides/mui-accordion.override';
import { muiBreadcrumbsOverride } from '../mui-overrides/mui-breadcrumbs.override';
import { muiButtonOverride } from '../mui-overrides/mui-button.override';
import { muiCheckboxOverride } from '../mui-overrides/mui-checkbox.override';
import { muiContainerOverride } from '../mui-overrides/mui-container.override';
import { muiDrawerOverride } from '../mui-overrides/mui-drawer.override';
import { muiFormControlLabelOverride } from '../mui-overrides/mui-form-control-label.override';
import { muiFormHelperTextOverride } from '../mui-overrides/mui-form-helper-text.override';
import { muiFormLabelOverride } from '../mui-overrides/mui-form-label.override';
import { muiGridOverride } from '../mui-overrides/mui-grid.override';
import { muiIconButtonOverride } from '../mui-overrides/mui-icon-button.override';
import { muiIconOverride } from '../mui-overrides/mui-icon.override';
import { muiLinkOverride } from '../mui-overrides/mui-link.override';
import { muiListItemIconOverride } from '../mui-overrides/mui-list-item-icon.override';
import { muiListOverride } from '../mui-overrides/mui-list.override';
import { muiPaginationItemOverride } from '../mui-overrides/mui-pagination-item.override';
import { muiPaginationOverride } from '../mui-overrides/mui-pagination.override';
import { muiRadioOverride } from '../mui-overrides/mui-radio.override';
import { muiTabOverride } from '../mui-overrides/mui-tab.override';
import { muiTabsOverride } from '../mui-overrides/mui-tabs.override';
import { muiToolbarOverride } from '../mui-overrides/mui-toolbar.override';
import { muiTooltipOverride } from '../mui-overrides/mui-tooltip.override';
import { muiTypographyOverride } from '../mui-overrides/mui-typography.override';
import { breakpoints } from '../variables/breakpoints';
import { darkTheme } from './dark-theme';
import { lightTheme } from './light-theme';

declare module '@mui/material/styles' {
  interface Palette {
    red: Record<number | string, string>;
    orange: Record<number | string, string>;
    green: Record<number | string, string>;
    blue: Record<number | string, string>;
    pink: Record<number | string, string>;
    beige: Record<number | string, string>;
    lightGreen: Record<number | string, string>;
    brown: Record<number | string, string>;
  }

  interface PaletteOptions {
    red?: Record<number | string, string>;
    orange?: Record<number | string, string>;
    green?: Record<number | string, string>;
    blue?: Record<number | string, string>;
    pink?: Record<number | string, string>;
    beige?: Record<number | string, string>;
    lightGreen?: Record<number | string, string>;
    brown: Record<number | string, string>;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    displayLG: true;
    displayMD: true;
    displaySM: true;
    headlineLGBold: true;
    headlineLGMedium: true;
    headlineLGItalic: true;
    headlineMDBold: true;
    headlineMDMedium: true;
    headlineSMBold: true;
    headlineSMMedium: true;
    subtitleLG: true;
    subtitleMD: true;
    bodyLGRegular: true;
    bodyLGSemibold: true;
    bodyMDRegular: true;
    bodyMDSemibold: true;
    bodySMRegular: true;
    bodySMSemibold: true;
    captionLGRegular: true;
    captionLGSemibold: true;
    captionMDRegular: true;
    captionMDSemibold: true;
    captionSMRegular: true;
    captionSMSemibold: true;
  }
}

export const theme = responsiveFontSizes(
  createTheme({
    spacing: 2,
    breakpoints,
    cssVariables: {
      colorSchemeSelector: 'class',
    },
    defaultColorScheme: 'light',
    colorSchemes: {
      light: lightTheme,
      dark: darkTheme,
    },
    typography: {
      fontFamily: 'inherit',
    },
    mixins: mixins,
    components: {
      MuiLink: muiLinkOverride,
      MuiDrawer: muiDrawerOverride,
      MuiIconButton: muiIconButtonOverride,
      MuiList: muiListOverride,
      MuiToolbar: muiToolbarOverride,
      MuiListItemIcon: muiListItemIconOverride,
      MuiGrid: muiGridOverride,
      MuiTypography: muiTypographyOverride,
      MuiButton: muiButtonOverride,
      MuiBreadcrumbs: muiBreadcrumbsOverride,
      MuiPagination: muiPaginationOverride,
      MuiPaginationItem: muiPaginationItemOverride,
      MuiTabs: muiTabsOverride,
      MuiTab: muiTabOverride,
      MuiRadio: muiRadioOverride,
      MuiFormControlLabel: muiFormControlLabelOverride,
      MuiIcon: muiIconOverride,
      MuiFormLabel: muiFormLabelOverride,
      MuiFormHelperText: muiFormHelperTextOverride,
      MuiCheckbox: muiCheckboxOverride,
      MuiAccordionSummary: muiAccordionSummaryOverride,
      MuiAccordion: muiAccordionOverride,
      MuiAccordionDetails: muiAccordionDetailsOverride,
      MuiTooltip: muiTooltipOverride,
      MuiContainer: muiContainerOverride,
    },
  }),
);
